/**
 * Nexora - Register Form Component
 *
 * Renders a modern, responsive registration form.
 * Handles validation and submission for new users.
 *
 * TODO: Update styles when theme system is integrated
 */

import AuthContext from '../auth/authContext.js'
import { normalizeAuthError, getAuthErrorType, allowsResendConfirmation } from '../auth/authErrorNormalization.js'
import { showToast } from '../../js/utils.js'

/**
 * Create Register Form HTML
 * Returns HTML string for registration form
 */
export const createRegisterForm = () => {
  return `
    <div class="auth-form-container">
      <div class="auth-form-card">
        <div class="auth-form-header">
          <img src="/icon-192.png" alt="NEXORA logo" class="auth-form-logo-img" />
          <h1 class="auth-form-title">NEXORA</h1>
          <p class="auth-form-subtitle">Créez votre compte</p>
        </div>

        <form id="registerForm" class="auth-form">
          <!-- Username Input -->
          <div class="form-group">
            <label for="registerUsername" class="form-label">Nom d'utilisateur</label>
            <input
              type="text"
              id="registerUsername"
              name="username"
              class="form-input"
              placeholder="ex: Utilisateur..."
              required
              autocomplete="off"
            />
            <span class="form-error" id="registerUsernameError"></span>
          </div>

          <!-- Email Input -->
          <div class="form-group">
            <label for="registerEmail" class="form-label">Email</label>
            <input
              type="email"
              id="registerEmail"
              name="email"
              class="form-input"
              placeholder="votre@email.com"
              required
              autocomplete="email"
            />
            <span class="form-error" id="registerEmailError"></span>
          </div>

          <!-- Password Input -->
          <div class="form-group">
            <label for="registerPassword" class="form-label">Mot de passe</label>
            <input
              type="password"
              id="registerPassword"
              name="password"
              class="form-input"
              placeholder="••••••••"
              required
              autocomplete="new-password"
            />
            <span class="form-error" id="registerPasswordError"></span>
            <div class="form-hint">Au moins 6 caractères</div>
          </div>

          <!-- Confirm Password Input -->
          <div class="form-group">
            <label for="registerPasswordConfirm" class="form-label">Confirmer le mot de passe</label>
            <input
              type="password"
              id="registerPasswordConfirm"
              name="passwordConfirm"
              class="form-input"
              placeholder="••••••••"
              required
              autocomplete="new-password"
            />
            <span class="form-error" id="registerPasswordConfirmError"></span>
          </div>

          <!-- Terms Checkbox -->
          <div class="form-group form-checkbox">
            <input
              type="checkbox"
              id="registerTerms"
              name="terms"
              required
            />
            <label for="registerTerms" class="checkbox-label">
              J'accepte les conditions d'utilisation
            </label>
            <span class="form-error" id="registerTermsError"></span>
          </div>

          <!-- Error Message -->
          <div class="form-error-box" id="registerErrorBox" style="display: none;">
            <span id="registerErrorMessage"></span>
          </div>

          <!-- Loading State -->
          <div class="form-loading" id="registerLoading" style="display: none;">
            <span class="spinner"></span>
            <span>Création du compte...</span>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            id="registerSubmitBtn"
            class="btn btn-primary btn-large"
            style="width: 100%; margin-top: 1rem;"
          >
            S'inscrire
          </button>
        </form>

        <!-- Link to Login -->
        <div class="auth-form-footer">
          <p>Vous avez déjà un compte? <a href="#" onclick="switchToLogin(event); return false;" class="auth-link">Se connecter</a></p>
          <p style="font-size: 12px; color: var(--text2); margin-top: 1rem;">
            🔐 Vos données seront chiffrées et stockées de façon sécurisée.
          </p>
        </div>
      </div>
    </div>
  `
}

/**
 * Attach Register Form Event Listeners
 * Sets up form submission and validation
 */
export const attachRegisterFormListeners = () => {
  const form = document.getElementById('registerForm')
  const usernameInput = document.getElementById('registerUsername')
  const emailInput = document.getElementById('registerEmail')
  const passwordInput = document.getElementById('registerPassword')
  const passwordConfirmInput = document.getElementById('registerPasswordConfirm')
  const termsCheckbox = document.getElementById('registerTerms')
  const submitBtn = document.getElementById('registerSubmitBtn')
  const errorBox = document.getElementById('registerErrorBox')
  const errorMessage = document.getElementById('registerErrorMessage')
  const loadingBox = document.getElementById('registerLoading')

  if (!form) return

  // Form submission
  form.addEventListener('submit', async (e) => {
    e.preventDefault()

    // Double-submit protection
    if (isSubmitting) {
      return
    }

    // Check rate limit cooldown
    const now = Date.now()
    if (now - lastSubmitTime < RATE_LIMIT_COOLDOWN) {
      const remainingSeconds = Math.ceil((RATE_LIMIT_COOLDOWN - (now - lastSubmitTime)) / 1000)
      errorMessage.textContent = `Attendez ${remainingSeconds} secondes avant de réessayer.`
      errorBox.style.display = 'flex'
      return
    }

    // Clear errors
    errorBox.style.display = 'none'
    loadingBox.style.display = 'none'
    clearAllErrors()

    const username = usernameInput.value.trim()
    const email = emailInput.value.trim()
    const password = passwordInput.value
    const passwordConfirm = passwordConfirmInput.value

    // Validation
    let hasError = false

    if (!username || username.length < 2) {
      document.getElementById('registerUsernameError').textContent = 'Au moins 2 caractères'
      hasError = true
    }

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      document.getElementById('registerEmailError').textContent = 'Email invalide'
      hasError = true
    }

    if (!password || password.length < 6) {
      document.getElementById('registerPasswordError').textContent = 'Au moins 6 caractères'
      hasError = true
    }

    if (password !== passwordConfirm) {
      document.getElementById('registerPasswordConfirmError').textContent = 'Les mots de passe ne correspondent pas'
      hasError = true
    }

    if (!termsCheckbox.checked) {
      document.getElementById('registerTermsError').textContent = 'Vous devez accepter les conditions'
      hasError = true
    }

    if (hasError) return

    // Set submitting state
    isSubmitting = true
    lastSubmitTime = now

    // Show loading state
    submitBtn.disabled = true
    loadingBox.style.display = 'flex'

    try {

      // Call auth context
      const { user, session, error } = await AuthContext.signUp(email, password, username)

      if (error) {
        const errorType = getAuthErrorType(error)
        const userMessage = normalizeAuthError(error)

        errorMessage.textContent = userMessage
        errorBox.style.display = 'flex'
        console.error('Register error:', error)

        // Show resend button if email not confirmed
        if (allowsResendConfirmation(errorType)) {
          showResendButton(email)
        }

        return
      }

      // Success - check if email confirmation is required
      if (user && !session) {
        // Email confirmation required
        showToast('✅ Compte créé! Vérifiez votre e-mail pour confirmer votre compte avant de vous connecter.')
        // Do not navigate to dashboard - user is not authenticated yet
        return
      }

      // Full signup with immediate session
      showToast('✅ Inscription réussie! Bienvenue ' + username + '!')

      // Simulate page transition to dashboard, but only if the user didn't
      // navigate elsewhere in the meantime.
      const _expectedHash = window.location.hash
      setTimeout(() => {
        const currentHash = window.location.hash
        const userAlreadyNavigated = currentHash && currentHash !== _expectedHash && currentHash !== '#section-dashboard'
        if (userAlreadyNavigated) return

        window.location.hash = '#section-dashboard'
        window.showSection?.('dashboard')
      }, 500)
    } catch (error) {
      console.error('Register exception:', error)
      errorMessage.textContent = 'Une erreur est survenue. Réessayez.'
      errorBox.style.display = 'flex'
    } finally {
      isSubmitting = false
      submitBtn.disabled = false
      loadingBox.style.display = 'none'
    }
  })

  // Real-time validation
  usernameInput.addEventListener('blur', () => {
    const username = usernameInput.value.trim()
    if (username && username.length < 2) {
      document.getElementById('registerUsernameError').textContent = 'Au moins 2 caractères'
    } else {
      document.getElementById('registerUsernameError').textContent = ''
    }
  })

  emailInput.addEventListener('blur', () => {
    if (emailInput.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailInput.value)) {
      document.getElementById('registerEmailError').textContent = 'Email invalide'
    } else {
      document.getElementById('registerEmailError').textContent = ''
    }
  })

  passwordInput.addEventListener('input', () => {
    if (passwordInput.value.length > 0 && passwordInput.value.length < 6) {
      document.getElementById('registerPasswordError').textContent = 'Au moins 6 caractères'
    } else {
      document.getElementById('registerPasswordError').textContent = ''
    }

    // Check confirm password match
    if (passwordConfirmInput.value && passwordInput.value !== passwordConfirmInput.value) {
      document.getElementById('registerPasswordConfirmError').textContent = 'Les mots de passe ne correspondent pas'
    } else {
      document.getElementById('registerPasswordConfirmError').textContent = ''
    }
  })

  passwordConfirmInput.addEventListener('input', () => {
    if (passwordConfirmInput.value && passwordInput.value !== passwordConfirmInput.value) {
      document.getElementById('registerPasswordConfirmError').textContent = 'Les mots de passe ne correspondent pas'
    } else {
      document.getElementById('registerPasswordConfirmError').textContent = ''
    }
  })

  termsCheckbox.addEventListener('change', () => {
    if (!termsCheckbox.checked) {
      document.getElementById('registerTermsError').textContent = 'Vous devez accepter les conditions'
    } else {
      document.getElementById('registerTermsError').textContent = ''
    }
  })
}

/**
 * Clear all error messages
 * @private
 */
const clearAllErrors = () => {
  document.getElementById('registerUsernameError').textContent = ''
  document.getElementById('registerEmailError').textContent = ''
  document.getElementById('registerPasswordError').textContent = ''
  document.getElementById('registerPasswordConfirmError').textContent = ''
  document.getElementById('registerTermsError').textContent = ''
}

/**
 * Double-submit protection
 * @private
 */
let isSubmitting = false
const RATE_LIMIT_COOLDOWN = 60000 // 60 seconds
let lastSubmitTime = 0

/**
 * Show resend confirmation button
 * @param {string} email - User email
 * @private
 */
const showResendButton = (email) => {
  const existingResend = document.getElementById('resendConfirmationBtn')
  if (existingResend) {
    existingResend.remove()
  }

  const resendBtn = document.createElement('button')
  resendBtn.id = 'resendConfirmationBtn'
  resendBtn.type = 'button'
  resendBtn.className = 'btn btn-outline'
  resendBtn.style.marginTop = '1rem'
  resendBtn.style.width = '100%'
  resendBtn.textContent = 'Renvoyer l\'e-mail de confirmation'

  resendBtn.addEventListener('click', async (e) => {
    e.preventDefault()

    // Check cooldown
    const now = Date.now()
    if (now - lastSubmitTime < RATE_LIMIT_COOLDOWN) {
      const remainingSeconds = Math.ceil((RATE_LIMIT_COOLDOWN - (now - lastSubmitTime)) / 1000)
      showToast(`⏱️ Attendez ${remainingSeconds} secondes avant de réessayer.`)
      return
    }

    resendBtn.disabled = true
    resendBtn.textContent = 'Envoi en cours...'

    try {
      // Call real Supabase Auth resend
      const { AuthService } = await import('../auth/authService.js')
      const { error } = await AuthService.resendConfirmationEmail(email)

      if (error) {
        throw error
      }

      showToast('✅ Si cette adresse est associée à un compte, un nouvel e-mail de confirmation a été envoyé.')

      // Update cooldown
      lastSubmitTime = Date.now()

      // Show cooldown
      const cooldownSeconds = Math.ceil(RATE_LIMIT_COOLDOWN / 1000)
      resendBtn.textContent = `Réessayer dans ${cooldownSeconds}s`

      // Enable button after cooldown
      setTimeout(() => {
        resendBtn.disabled = false
        resendBtn.textContent = 'Renvoyer l\'e-mail de confirmation'
      }, RATE_LIMIT_COOLDOWN)

    } catch (error) {
      console.error('Resend error:', error)
      const errorType = getAuthErrorType(error)
      const userMessage = normalizeAuthError(error)
      showToast(`❌ ${userMessage}`)
      resendBtn.disabled = false
      resendBtn.textContent = 'Renvoyer l\'e-mail de confirmation'
    }
  })

  errorBox.parentNode.insertBefore(resendBtn, errorBox.nextSibling)
}

export default { createRegisterForm, attachRegisterFormListeners }
